import { addB2bAPIParams } from '../b2b';
import { QUERY_STRING_KEY } from '../constants/app';
import { getOnClickRecentSearches } from '../instant-search-widget/recent-search.js';
import { getSearchTerm } from '../integration/multiple-language';
import { restrictMainCurrency } from '../shopify-market/restrict-user.js';
import {
  addParamsLocale,
  getCurrentPage,
  getCustomerId,
  getSearchSettings,
  getTenantId,
  setLocalStorage,
  stripHtml,
} from '../utils';
import { saveRequestId } from '.';

/**
 * @type {import('./index.d').buildQueryParams}
 */
const buildQueryParams = (params) => {
  const queryParams = new URLSearchParams();
  queryParams.sort();
  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          queryParams.append(`${key}`, item);
        });
      } else {
        queryParams.set(key, value);
      }
    }
  }
  // handle custom parameters
  const customParams = window.boostSdCustomParams;
  if (customParams && Object.keys(customParams).length > 0) {
    for (const key in customParams) {
      queryParams.set(key, customParams[key]);
    }
  }

  queryParams.set('customer_id', getCustomerId());

  return queryParams;
};

/**
 * @type {import('./index.d').getProductsDetail}
 */
export const getProductsDetail = async (context, ids) => {
  try {
    const url = context.app.searchUrl;
    const searchSettings = getSearchSettings(context);
    const { productAvailable } = searchSettings;
    let params = {
      shop: getTenantId(),
      locale: context.app?.generalSettings?.current_locale || 'en',
      event_type: 'init',
      ids,
    };

    if (productAvailable) {
      params.product_available = productAvailable;
    }

    if (context.app.b2b?.enabled) {
      addB2bAPIParams(params, context);
    }

    params = addParamsLocale(params);

    const response = await fetch(`${url}/products?${buildQueryParams(params)}`, {
      method: 'GET',
    });

    const jsonRes = await response.json();

    if (jsonRes.meta) {
      const { money_format, money_format_with_currency, currency } = jsonRes.meta;
      restrictMainCurrency(context, { money_format, money_format_with_currency, currency });
    }

    return jsonRes;
  } catch (error) {
    console.log('Can not get list suggestion product');
    return {};
  }
};

/**
 * @type {import('./index.d').getProductsDetailByHandle}
 */
export const getProductsDetailByHandle = async (context, handle, searchQuery) => {
  try {
    const url = context.app.searchUrl;
    const searchSettings = getSearchSettings(context);
    const { productAvailable } = searchSettings;
    const params = {
      shop: getTenantId(),
      handle,
      locale: context.app?.generalSettings?.current_locale || 'en',
      event_type: 'init',
    };

    if (productAvailable) {
      params.product_available = productAvailable;
    }

    if (context.app.b2b?.enabled) {
      addB2bAPIParams(params, context);
    }

    const response = await fetch(`${url}/products`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    const jsonRes = await response.json();

    if (jsonRes.meta) {
      const { money_format, money_format_with_currency, currency } = jsonRes.meta;
      restrictMainCurrency(context, { money_format, money_format_with_currency, currency });
    }

    return jsonRes;
  } catch (error) {
    console.log('Can not get list suggestion product');
  }
};

/**
 * @type {import('./index.d').getMostPopularProductTemplate}
 */
export const getMostPopularProductTemplate = async (context, handle) => {
  try {
    const url = context.app.searchUrl;
    url?.replace('search', 'ssr-template');

    const widgetName = 'mostPopularProducts';
    const widgetId = context?.app?.templateMetadata?.[widgetName];
    const themeLib = context?.app?.templateMetadata?.themeNameLib || 'default';

    let params = {
      shop: getTenantId(),
      locale: context.app?.generalSettings?.current_locale || 'en',
      event_type: 'init',
      handle,
      widget: `${widgetName}:${widgetId}`,
      theme_lib: themeLib,
    };

    params = addParamsLocale(params);

    if (context.app.b2b?.enabled) {
      addB2bAPIParams(params, context);
    }

    const response = await fetch(`${url}/products?${buildQueryParams(params)}`, {
      method: 'GET',
    });

    const jsonRes = await response.json();

    if (jsonRes.meta) {
      const { money_format, money_format_with_currency, currency } = jsonRes?.meta || {};
      restrictMainCurrency(context, { money_format, money_format_with_currency, currency });
    }

    return jsonRes;
  } catch (error) {
    console.log('Can not get mostPopularProducts template', error);
    return {};
  }
};

/**
 * @type {import('./index.d').getSuggestionSearch}
 */
export const getSuggestionSearch = async (context, query, customizeSearchParams = {}) => {
  try {
    const url = context.app.searchUrl;
    const {
      suggestionDymLimit,
      enableDefaultResult,
      enableFuzzySearch,
      productAvailable,
      suggestionMode,
      skipFields,
      reduceMinMatch,
      fullMinMatch,
      enablePlusCharacterSearch,
      suggestionBlocks,
      showVariantsAsProduct,
    } = context.app?.searchSettings || {};

    let params = {
      t: Date.now(),
      shop: getTenantId(),
      locale: context.app?.generalSettings?.current_locale || 'en',
      q: stripHtml(query),
      // sid: getBoostPFSSectionId(),
      re_run_if_typo: true, // field did you mean improve
      event_type: 'suggest',
      pg: getCurrentPage(),
    };

    // Integrate with weglot
    if (window.boostWidgetIntegration?.integration?.languages === 'weglot') {
      params.q = await getSearchTerm(params.q);
    }

    params = addParamsLocale(params);

    // limit
    if (Array.isArray(suggestionBlocks)) {
      // { type: 'suggestions', label: 'Suggestions', status: 'active', number: 3 },
      suggestionBlocks.forEach((block) => {
        const id = block['type'].slice(0, -1);
        params[`${id}_limit`] = block['number'];
      });
    }

    const recentSearch = getOnClickRecentSearches();
    if (recentSearch.length > 0) {
      params.recent_search = recentSearch.map((r) => r.title).join(',');
    }

    if (suggestionMode) params.suggestionMode = suggestionMode;
    if (skipFields && skipFields.length > 0) params.skipFields = skipFields;
    if (suggestionDymLimit) params['dym_limit'] = suggestionDymLimit;
    if (enableFuzzySearch !== true) params.fuzzy = enableFuzzySearch;
    if (reduceMinMatch !== false) params.reduce_min_match = reduceMinMatch;
    if (fullMinMatch) params.full_min_match = true;
    if (enablePlusCharacterSearch) params.enable_plus_character_search = true;
    if (!enableDefaultResult) params.enable_default_result = false;
    // Show variants as products
    if (showVariantsAsProduct) params.variants_as_products = true;
    // Get product/variant available or not field. ! of Fields Out of stock products in instant search
    if (productAvailable) params.product_available = true;
    if (context.app.b2b?.enabled) {
      addB2bAPIParams(params, context);
    }
    // allow customize search params with any params
    if (customizeSearchParams && Object.keys(customizeSearchParams)?.length > 0) {
      params = {
        ...params,
        ...customizeSearchParams,
      };
    }

    const response = await fetch(`${url}/suggest?${buildQueryParams(params)}`, {
      method: 'GET',
    });

    const jsonRes = await response.json();

    // TODO Check when api return meta rid
    const requestId = await jsonRes.meta?.rid;
    saveRequestId('suggest', requestId, jsonRes.bundles);
    setLocalStorage(QUERY_STRING_KEY, `q=${stripHtml(query)}`);

    //Generate newId for customize
    const newId = window.boostWidgetIntegration.generateUuid();
    context.state.latestInstantSearchRequest = newId;

    if (jsonRes.meta) {
      const { money_format, money_format_with_currency, currency } = jsonRes.meta;
      restrictMainCurrency(context, { money_format, money_format_with_currency, currency });
    }

    return jsonRes;
  } catch (error) {
    console.log('Can not get suggestion data', error);
    return { query };
  }
};

/**
 * @type {import('./index.d').getRedirects}
 */
export const getRedirects = async (context, terms = []) => {
  try {
    const url = context.app.searchUrl;
    let params = {
      t: Date.now(),
      shop: getTenantId(),
      locale: context.app?.generalSettings?.current_locale || 'en',
      build_filter_tree: true,
      // sid: getBoostPFSSectionId(),
      pg: getCurrentPage(),
      terms,
      limit: 0, // not limit record from API redirects.
    };

    if (context.app.b2b?.enabled) {
      addB2bAPIParams(params, context);
    }
    params = addParamsLocale(params);

    const response = await fetch(`${url}/redirects?${buildQueryParams(params)}`, {
      method: 'GET',
    });

    const responseData = await response.json();
    const redirects = {};

    responseData.data?.forEach((redirect) => {
      redirect?.terms?.forEach((term) => {
        redirects[term] = redirect.redirect_to;
      });
    });

    if (responseData.meta) {
      const { money_format, money_format_with_currency, currency } = responseData.meta;
      restrictMainCurrency(context, { money_format, money_format_with_currency, currency });
    }

    return redirects;
  } catch (error) {
    console.log('Can not get list of redirect');
    return {};
  }
};
